import {
  INTERNAL_ORG_SLUG,
  organizationList,
  studyAccessView,
  studySummary,
} from "@ir/contracts";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { StudyForm } from "@/features/studies/StudyForm";
import { ApiError, apiFetch } from "@/lib/api";
import { canManageStudies } from "@/lib/roles";
import { authClient } from "@/lib/authClient";

export const Route = createFileRoute("/_auth/admin/estudios/$id")({
  async beforeLoad() {
    const { data } = await authClient.getSession();
    if (!canManageStudies(data?.user.role)) redirect({ to: "/insights", throw: true });
  },
  component: EditStudyPage,
});

function EditStudyPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();

  // Sin reintentos en las dos consultas por id: un id que no existe responde
  // 404, y reintentarlo tres veces solo retrasa el error sin cambiar el
  // resultado. Es lo que corrige la pregunta de autorrevisión de la Task 13
  // ("¿qué pasa si abre /admin/estudios/$id con un id que no existe?"): sin
  // `retry: false` NI el chequeo de `isError` de abajo, `study.data` se
  // queda en `undefined` para siempre y la pantalla muestra "Cargando…" sin
  // fin, nunca el error del servidor.
  const study = useQuery({
    queryKey: ["study", id],
    queryFn: () => apiFetch(`/api/studies/${id}`, { schema: studySummary }),
    retry: false,
  });
  const access = useQuery({
    queryKey: ["study", id, "access"],
    queryFn: () => apiFetch(`/api/studies/${id}/access`, { schema: studyAccessView }),
    retry: false,
  });
  const organizations = useQuery({
    queryKey: ["organizations"],
    queryFn: () => apiFetch("/api/organizations", { schema: organizationList }),
    retry: false,
  });

  if (study.isError || access.isError || organizations.isError) {
    const error = study.error ?? access.error ?? organizations.error;
    return (
      <p className="px-8 py-6 text-nota text-warn">
        {error instanceof ApiError ? error.message : t("common.error")}
      </p>
    );
  }

  // Por slug, no por `kind`: ver la nota en INTERNAL_ORG_SLUG.
  const interna = organizations.data?.items.find((o) => o.slug === INTERNAL_ORG_SLUG);
  if (!study.data || !access.data || !interna) {
    return <p className="px-8 py-6 text-nota text-ink3">{t("common.loading")}</p>;
  }

  return (
    <StudyForm
      study={study.data}
      initialAccess={access.data}
      internalOrganizationId={interna.id}
    />
  );
}
